import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useFmt } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { resolveImageUrl } from "../components/ImageUpload";
import StarRating from "../components/StarRating";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const fmtJMD = useFmt();

  const loadAll = () => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
    api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [id]);

  if (product === null) return <div className="py-32 text-center text-zinc-500">Loading...</div>;
  if (product === false) return <div className="py-32 text-center text-zinc-500">Flavour not found.</div>;

  const soldOut = product.in_stock === false || product.stock === 0;
  const canReview = !!user && user.role === "customer";

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewError("");
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_id: id, rating, comment });
      toast.success("Review submitted — pending approval");
      setComment("");
      setRating(5);
      loadAll();
    } catch (e) {
      setReviewError(formatApiError(e.response?.data?.detail) || "Could not submit");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <SEO
        title={product.seo_title || product.name}
        description={product.seo_description || product.description || `${product.name} — real-fruit Jamaican popsicle, JMD $${product.price}.`}
        image={resolveImageUrl(product.og_image_url || product.image_url)}
        type="product"
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-12 py-12 sm:py-16" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-6 sm:mb-8" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        <div className="rounded-3xl overflow-hidden border border-zinc-100 bg-white">
          <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-full h-72 sm:h-96 lg:h-[520px] object-cover" />
        </div>
        <div>
          <div className="overline text-zinc-500 mb-3">Flavour</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight break-words">{product.name}</h1>
          {(product.rating_count || 0) > 0 && (
            <div className="mt-3">
              <StarRating rating={product.rating_avg || 0} size={16} showNumber count={product.rating_count} />
            </div>
          )}
          <p className="mt-3 sm:mt-4 text-zinc-600 text-base sm:text-lg leading-relaxed">{product.description}</p>
          <div className="mt-6 sm:mt-8 font-display text-2xl sm:text-3xl font-bold">{fmtJMD(product.price)}</div>
          <div className="mt-2 text-sm text-zinc-500">
            {soldOut ? <span className="text-red-500 font-semibold">Sold out</span> : <span className="text-green-600 font-semibold">In stock</span>}
          </div>

          <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3 border border-zinc-200 rounded-full px-2">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2" data-testid="qty-decrement"><Minus size={14} /></button>
              <span className="w-6 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2" data-testid="qty-increment"><Plus size={14} /></button>
            </div>
            <button
              disabled={soldOut}
              onClick={() => {
                addItem({
                  type: "product", ref_id: product.id, name: product.name,
                  price: product.price, quantity: qty, image_url: product.image_url,
                });
                toast.success(`${qty} × ${product.name} added to cart`);
              }}
              className="btn-primary disabled:opacity-50"
              data-testid="add-to-cart-btn">
              Add to cart
            </button>
          </div>

          <div className="mt-6 sm:mt-8">
            <ShareButtons title={product.name} testid="product-share" />
          </div>
        </div>
      </div>

      {/* Reviews section */}
      <section className="mt-14 sm:mt-20 border-t border-zinc-200 pt-10 sm:pt-12" data-testid="product-reviews">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6 sm:mb-8">
          <div>
            <div className="overline text-zinc-500 mb-2">Customer reviews</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              What people are saying
            </h2>
          </div>
          {(product.rating_count || 0) > 0 && (
            <div className="text-right">
              <div className="font-display text-3xl font-bold">
                {(product.rating_avg || 0).toFixed(1)}
                <span className="text-sm text-zinc-400 font-normal"> / 5</span>
              </div>
              <StarRating rating={product.rating_avg || 0} size={14} className="justify-end mt-1" />
              <div className="text-xs text-zinc-500 mt-1">{product.rating_count} review{product.rating_count !== 1 ? "s" : ""}</div>
            </div>
          )}
        </div>

        {/* Leave a review */}
        {canReview ? (
          <form onSubmit={submitReview} className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6 mb-8" data-testid="leave-review-form">
            <div className="font-display font-bold mb-3">Rate this flavour</div>
            <p className="text-xs text-zinc-500 mb-3">Reviews are visible only after they've been delivered to you and approved by our team.</p>
            <div className="mb-4">
              <StarRating rating={rating} onChange={setRating} size={28} testid="review-stars" />
            </div>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you love about it? (optional)"
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm"
              data-testid="review-comment"
            />
            {reviewError && <div className="text-sm text-red-600 mt-2" data-testid="review-error">{reviewError}</div>}
            <button type="submit" disabled={submitting} className="btn-primary mt-4 disabled:opacity-50" data-testid="submit-review-btn">
              {submitting ? "Sending…" : "Submit review"}
            </button>
          </form>
        ) : (
          <div className="rounded-2xl border border-dashed border-zinc-200 p-5 sm:p-6 mb-8 text-sm text-zinc-500 flex flex-wrap gap-3 items-center justify-between">
            <span>Sign in as a customer to leave a review (only after a delivered order).</span>
            <Link to="/login" className="btn-primary text-sm" data-testid="login-to-review">Sign in</Link>
          </div>
        )}

        {/* Reviews list */}
        {reviews.length === 0 ? (
          <div className="text-sm text-zinc-400 italic">No reviews yet — be the first.</div>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  {r.user_avatar_url ? (
                    <img src={resolveImageUrl(r.user_avatar_url)} alt={r.user_name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#F74B64] text-white flex items-center justify-center text-xs font-bold">
                      {(r.user_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{r.user_name}</div>
                    <div className="flex items-center gap-2">
                      <StarRating rating={r.rating} size={12} />
                      <span className="text-[10px] text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                {r.comment && <p className="text-sm text-zinc-700 break-words">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
    </>
  );
}
